import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export const MAP_MARKER_COLOR = {
  pending: '#f59e0b',
  confirmed: '#10b981',
  rejected: '#ef4444',
};

// Same idea as a per-scan "View location" link (a plain pin on a map), just
// with every scan's pin dropped on one shared map instead of opening a new
// tab per student. No Maps API key is configured for this app, so this
// renders its own Leaflet map over OpenStreetMap tiles rather than relying
// on a keyed embed. Shared between the lecturer's live QR page and the
// "Scanned Attendance" summary popup on the schedule list.
export default function AllLocationsMap({ scans, onClose }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const hasFitRef = useRef(false);

  // Create the map exactly once for the life of this modal. A caller's scans
  // list can keep refreshing (e.g. a live poll) -- tearing the whole map
  // down and rebuilding it on every one of those updates could hit a
  // teardown mid-animation (e.g. a fitBounds zoom still in flight) and crash
  // with "Cannot read properties of undefined (reading '_leaflet_pos')".
  // Markers are updated separately below without touching the map instance.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { scrollWheelZoom: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    map.setView([0, 0], 2);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Redraw markers whenever the scans list changes, in place on the existing
  // map -- no re-init, no interrupted animations.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current = [];

    const points = scans.filter((s) => s.latitude !== null && s.latitude !== undefined && s.longitude !== null && s.longitude !== undefined);

    points.forEach((s) => {
      const color = MAP_MARKER_COLOR[s.status] || MAP_MARKER_COLOR.pending;
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid #ffffff;box-shadow:0 0 0 1px rgba(15,23,42,0.25);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const marker = L.marker([Number(s.latitude), Number(s.longitude)], { icon }).addTo(map);
      const accuracyText = s.accuracy ? ` &middot; &plusmn;${Math.round(s.accuracy)}m` : '';
      marker.bindPopup(`<strong>${s.studentName || s.studentId}</strong><br/>${s.studentId}<br/>${s.status}${accuracyText}`);
      markersRef.current.push(marker);
    });

    // Only auto-fit once, on first data -- otherwise every poll would yank
    // the view back and undo any panning/zooming the viewer just did.
    if (points.length > 0 && !hasFitRef.current) {
      // The container's size can still be settling right as the modal
      // mounts; without this, fitBounds can compute its zoom against a
      // stale (often zero) size and land way too far zoomed out.
      map.invalidateSize();
      if (points.length === 1) {
        // fitBounds on a single (zero-area) point doesn't reliably zoom in
        // close -- just center on it at a fixed close zoom instead.
        map.setView([Number(points[0].latitude), Number(points[0].longitude)], 18);
      } else {
        map.fitBounds(L.latLngBounds(points.map((s) => [Number(s.latitude), Number(s.longitude)])), { padding: [60, 60], maxZoom: 18 });
      }
      hasFitRef.current = true;
    }

    return undefined;
  }, [scans]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">All Scan Locations</h3>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div ref={containerRef} className="h-[500px] w-full rounded-xl border border-slate-200" />
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: MAP_MARKER_COLOR.pending }} /> Pending</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: MAP_MARKER_COLOR.confirmed }} /> Confirmed</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: MAP_MARKER_COLOR.rejected }} /> Rejected</span>
        </div>
      </div>
    </div>
  );
}
